import { Grid } from '@mui/material';
import { IFurnitureRequest, gridTags } from 'app/models/Placement/PlacementMods';
import React, { useEffect, useState } from 'react';
import { getFormattedFurniture } from '../../ModificationsUtils';
import { useTranslation } from 'react-i18next';
import { getFurnitureGridColumns } from './helper';
import { usePromiseTracker } from 'react-promise-tracker';
import { XGrid } from '@AMIEWEB/Common/XGrid/XGrid';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentModDetails,
  selectEditFurnitureRows,
  selectFurnitureQuantityError,
  selectFurnitureCostToTravelerError,
  selectIsEditMod,
  selectIsUndoModal,
} from 'store/redux-store/placement-mods/selectors';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { furnitureModStyles } from './FurnitureMod.styles';
export const FurnitureGrid = ({
  furnitureRequests,
  isExpanded,
}: {
  furnitureRequests: IFurnitureRequest[];
  isExpanded: boolean;
}) => {
  const { t } = useTranslation();
  const { classes } = furnitureModStyles({ isExpanded });
  const isModEdit = useSelector(selectIsEditMod);
  const dispatch = useDispatch();
  const { promiseInProgress: isCallingAPI } = usePromiseTracker({ area: 'furniture-details-api', delay: 0 });
  const selectedModsDetails = useSelector(selectCurrentModDetails);
  const furnitureEditRequests = useSelector(selectEditFurnitureRows);
  const [filteredRows, setFilteredRow] = useState([]);
  //@Team - @Krishna Gond - please use constants of use state if state is not updated
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [columns, setColumns] = useState<any>(
    getFurnitureGridColumns(t, isExpanded, isModEdit, furnitureEditRequests, classes),
  );
  const [headerCount, setHeaderCount] = useState<number>(0);
  const [secondaryCount, setSecondaryCount] = useState<number>(0);
  const furnitureQuantityErrors = useSelector(selectFurnitureQuantityError);
  const furnitureCostToTravelerErrors = useSelector(selectFurnitureCostToTravelerError);
  const isUndoModal = useSelector(selectIsUndoModal);
  useEffect(() => {
    const initialColumns = getFurnitureGridColumns(t, isExpanded, isModEdit, furnitureEditRequests, classes);
    setColumns(initialColumns);
    setFilteredRow(selectedModsDetails?.furniture);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, isExpanded, isModEdit, furnitureEditRequests]);

  const getRowsCount = model => {
    setFilteredRow(model?.items);
  };

  const handleRefreshGrid = () => {
    dispatch(
      placementModsAction.getFurnitureModDetails({
        placementId: selectedModsDetails?.placementId,
        travelerOptionId: selectedModsDetails?.travelerOptionId,
      }),
    );
  };

  const getWidth = () => {
    if (isCallingAPI || !isExpanded) {
      return '780px';
    } else {
      return null;
    }
  };

  const resetColumns = () => {
    setColumns(getFurnitureGridColumns(t, isExpanded, isModEdit, furnitureEditRequests, classes));
  };

  const getHeaderCount = furnitureEditRequests => {
    const totalRowCount = furnitureEditRequests?.length;
    setHeaderCount(totalRowCount);
  };

  const getSecondaryCount = furnitureEditRequests => {
    const includedRowCount = furnitureEditRequests?.filter(each => each?.include)?.length;
    setSecondaryCount(includedRowCount);
  };
  useEffect(() => {
    if (isUndoModal) {
      dispatch(placementModsAction.setFurnitureQuantityError([]));
      dispatch(placementModsAction.setFurnitureCostToTravelError([]));
      dispatch(placementModsAction.setGridModError(false));
      dispatch(placementModsAction.setIsUndoModal(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUndoModal]);
  useEffect(() => {
    if (furnitureEditRequests?.length === 0) {
      dispatch(placementModsAction.setGridModError(true));
    } else if (
      furnitureQuantityErrors.some(val => val.quantity) ||
      furnitureCostToTravelerErrors.some(val => val.costToTravel)
    ) {
      dispatch(placementModsAction.setGridModError(true));
    } else {
      dispatch(placementModsAction.setGridModError(false));
    }
    getHeaderCount(isModEdit ? furnitureEditRequests : furnitureRequests);
    getSecondaryCount(isModEdit ? furnitureEditRequests : furnitureRequests);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [furnitureQuantityErrors, furnitureCostToTravelerErrors, furnitureEditRequests]);

  const FurnitureGrid = React.useMemo(() => {
    return (
      <Grid style={{ paddingBottom: '24px' }}>
        <XGrid
          gridTag={gridTags.FURNITUREGRIDTAG}
          rows={
            isModEdit
              ? getFormattedFurniture(furnitureEditRequests) || []
              : getFormattedFurniture(furnitureRequests) || []
          }
          columns={columns}
          disableSelectionOnClick
          editMode="row"
          pagination={false}
          autoHeight={true}
          sx={{ minWidth: '700px' }}
          hideFooter
          inDialog
          title={t('placement.placementMods.furniture')}
          secondaryTitle={`/ ${t('placement.placementMods.included')} (${secondaryCount})`}
          displayCustomCount={selectedIds?.length > 0 ? selectedIds?.length : headerCount}
          loading={isCallingAPI}
          className={classes.expandedGrid}
          onFilterModelChange={model => getRowsCount(model)}
          toolbarProps={{
            refreshButton: true,
            columnSelector: isModEdit ? false : true,
            resetButton: true,
            resetColumns: () => {
              resetColumns();
              dispatch(
                globalActions.setSnackBar({
                  message: 'Columns have been reset.',
                  severity: 'success',
                }),
              );
            },
            refreshGrid: () => {
              handleRefreshGrid();
            },
          }}
        />
      </Grid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [furnitureRequests, isExpanded, isCallingAPI, resetColumns, columns, filteredRows, furnitureEditRequests]);

  return (
    <Grid item xs={12} sx={{ width: getWidth() }}>
      {FurnitureGrid}
    </Grid>
  );
};
